/**
 * The recent-alarm store behind the toasts and the notification bell.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT DECIDES WHETHER SOMETHING INTERRUPTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Not everything on the stream deserves a toast, and this is the whole design problem. A
 * toast is an interruption: it covers content, it moves the eye, and an operator who gets
 * one for something they do not care about learns to dismiss them without reading — at
 * which point the mechanism is worse than not having it, because the one that mattered is
 * dismissed the same way.
 *
 * So:
 *   `opened`    TOASTS. A new condition on the plant, which is the thing this exists for.
 *   `reopened`  TOASTS. A condition that cleared and came back is not the same as one that
 *               never cleared, and it is often worse — an intermittent fault.
 *   `closed`    TOASTS, quietly and briefly. Being told the thing you were watching has
 *               gone away is worth an interruption; being told at the same volume as the
 *               fault is not.
 *   `changed`   NEVER TOASTS. Lifecycle moves — acknowledge, assign, note, verify — are
 *               things a PERSON just did, usually the person looking at the screen. A
 *               toast for your own click is noise, and on a busy board with three
 *               operators triaging it is a stream of noise.
 *
 * Everything lands in the bell regardless. The bell is a log, and a log that omitted the
 * quiet events would be a worse log.
 */
import { create } from 'zustand';
import type { LiveAlarm, StreamState } from '@/lib/api/events';

/**
 * How many events the bell remembers.
 *
 * Bounded because this is session state in memory, and an unbounded list on a fleet with a
 * chattering device is a leak that presents as the tab slowing down over a shift. Fifty is
 * more than fits on the screen and less than anybody scrolls.
 */
const KEEP = 50;

/**
 * How long a toast stays, by kind.
 *
 * `closed` is deliberately the shortest: it is good news, and good news that lingers is in
 * the way. An opened alarm stays long enough to be read and acted on rather than glimpsed.
 */
/**
 * How many toasts may be on screen at once.
 *
 * Five fills roughly half a 900px viewport at this card height, which leaves the console
 * usable while an alarm storm is in progress. It is a display limit and not a data limit:
 * everything still reaches `recent` and the unseen count.
 */
export const MAX_TOASTS = 5;

export const TOAST_MS: Record<LiveAlarm['event'], number> = {
  opened: 9_000,
  reopened: 9_000,
  closed: 5_000,
  changed: 0,
  /* The longest: it is addressed to this person and asks them to act. */
  reminder: 12_000,
};

export interface ToastItem extends LiveAlarm {
  /** Stable across re-renders, and unique per ARRIVAL rather than per alarm. */
  key: string;
}

interface FeedState {
  /** Newest first. The bell's list. */
  recent: LiveAlarm[];
  /** Currently on screen. */
  toasts: ToastItem[];
  /** Cleared when the bell is opened. */
  unseen: number;
  stream: StreamState;

  push: (a: LiveAlarm) => void;
  dismiss: (key: string) => void;
  markSeen: () => void;
  setStream: (s: StreamState) => void;
}

let seq = 0;

export const useAlarmFeed = create<FeedState>((set, get) => ({
  recent: [],
  toasts: [],
  unseen: 0,
  stream: 'connecting',

  push: (a) => {
    const { recent, toasts, unseen } = get();

    /*
     * DEDUPE ON (id, event), and this is not theoretical.
     *
     * The stream reconnects, and the trigger fires on every UPDATE — so the same
     * transition can arrive twice: once live, once because a retried write touched the row
     * again. Two identical toasts for one event reads as two events, which on an alarm
     * board is a wrong number in front of somebody making a decision.
     *
     * Keyed on the pair rather than the id alone, because an alarm that opens, closes and
     * reopens is three genuine events on one id.
     */
    const already = recent.some((r) => r.id === a.id && r.event === a.event && r.rule === a.rule);

    const nextRecent = already ? recent : [a, ...recent].slice(0, KEEP);
    const toastable = TOAST_MS[a.event] > 0 && !already;

    seq += 1;
    set({
      recent: nextRecent,
      unseen: already ? unseen : unseen + 1,
      /*
       * CAPPED. `recent` was bounded at KEEP and `toasts` was not, so on a chattering device
       * the stack grew without limit up a `fixed bottom-4 right-4` column that has no
       * overflow handling — off the top of the viewport, over the page, unclosable because
       * the dismiss buttons had scrolled out of reach.
       *
       * The OLDEST are dropped, not the newest: a toast is an interruption about something
       * that just happened, and the one an operator has already had nine seconds to read is
       * the one they need least. Nothing is lost either way — every alarm is in `recent`
       * behind the bell, which is what that list is for.
       */
      toasts: toastable
        ? [...toasts, { ...a, key: `${a.id}:${a.event}:${seq}` }].slice(-MAX_TOASTS)
        : toasts,
    });
  },

  dismiss: (key) => set({ toasts: get().toasts.filter((t) => t.key !== key) }),
  markSeen: () => set({ unseen: 0 }),
  setStream: (s) => set({ stream: s }),
}));
